import { Card } from './Card';
import { IPieChartDataItem, PieChart } from '@src/components';
import { SimUsage } from '@src/services/dashboard/simUsages/simUsagesApiSliceTypes';
import { InUseSim } from '@src/services/dashboard/inUseSims/inUseSimsApiSliceTypes';
import { useMemo } from 'react';
import { InUseStackedBarChart } from './InUseStackedBarChart';
import { DataUsageStackedBarChart } from './DataUsageStackedBarChart';
import { chartColors } from '@src/constants/chartColors';
import { InUseSimTotal } from '@src/services/dashboard/inUseSimsTotal/inUseSimsTotalApiSliceTypes';
import { fixNumberPrecision } from '@src/utilities/currency';
import {
    ChartPieIcon,
    ClockIcon,
    CpuChipIcon,
} from '@heroicons/react/24/solid';

type SubAccountsChartsProps = {
    dataSimUsage: SimUsage[];
    dataInUseSims: InUseSim[];
    dataInUseSimsTotal: InUseSimTotal[];
};
export const SubAccountsCharts = ({
    dataSimUsage,
    dataInUseSims,
    dataInUseSimsTotal,
}: SubAccountsChartsProps) => {
    function stringToColor(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % chartColors.length;
        return chartColors[index];
    }

    const inUseSimsDoughnutData: IPieChartDataItem[] = useMemo(() => {
        if (!dataInUseSimsTotal) return [];

        return dataInUseSimsTotal.map((item, index) => {
            const key = item.accountName || 'Unassigned';
            return {
                value: item.totalInUse,
                name: item.accountName,
                color: stringToColor(key),
            };
        });
    }, [dataInUseSimsTotal]);

    const simUsageDoughnutData: IPieChartDataItem[] = useMemo(() => {
        if (!dataSimUsage) return [];

        const grouped = dataSimUsage.reduce<Record<string, number>>(
            (acc, item) => {
                const key = item.accountName || 'Unassigned';
                acc[key] = (acc[key] || 0) + item.totalDataUsage;
                return acc;
            },
            {}
        );

        return Object.entries(grouped).map(([name, value]) => ({
            name,
            value,
            color: stringToColor(name),
        }));
    }, [dataSimUsage]);

    const aggregatedSimUsage = useMemo(() => {
        if (!dataSimUsage) return [];

        const grouped = dataSimUsage.reduce<Record<string, number>>(
            (acc, item) => {
                const key = item.accountName || 'Unassigned';
                acc[key] = (acc[key] || 0) + item.totalDataUsage;
                return acc;
            },
            {}
        );

        return Object.entries(grouped).map(([accountName, totalDataUsage]) => ({
            accountName,
            totalDataUsage,
        }));
    }, [dataSimUsage]);

    return (
        <div className="flex flex-col gap-8 my-8">
            <div className="flex flex-col md:flex-row w-full gap-4">
                <Card
                    title="In-Use SIMs"
                    className="w-full md:1/2 lg:w-3/5 xl:w-4/6"
                    icon={
                        <CpuChipIcon className="w-6 h-6 text-primary opacity-45" />
                    }
                    content={
                        <InUseStackedBarChart
                            data={dataInUseSims}
                            height={230}
                            colors={chartColors}
                        />
                    }
                />

                <Card
                    title="In-Use SIMs (Per account)"
                    className="w-full md:1/2 lg:w-2/5 xl:w-2/6"
                    icon={
                        <ClockIcon className="w-6 h-6 text-primary opacity-45" />
                    }
                    content={
                        <PieChart
                            key="subAccounts"
                            name=""
                            data={inUseSimsDoughnutData}
                            height={175}
                            className="p-4 md:p-1"
                            total={fixNumberPrecision(
                                inUseSimsDoughnutData?.reduce(
                                    (acc, item) => acc + item.value,
                                    0
                                )
                            )}
                            totalText="SIMs"
                        />
                    }
                />
            </div>
            <div className="flex flex-col md:flex-row w-full gap-4">
                <Card
                    title="Data usage"
                    className="w-full md:1/2 lg:w-3/5 xl:w-4/6"
                    icon={
                        <ChartPieIcon className="w-6 h-6 text-primary opacity-45" />
                    }
                    content={
                        <DataUsageStackedBarChart
                            data={aggregatedSimUsage}
                            height={230}
                            colors={chartColors}
                        />
                    }
                />

                <Card
                    title="Data usage (Per account)"
                    className="w-full md:1/2 lg:w-2/5 xl:w-2/6"
                    icon={
                        <ClockIcon className="w-6 h-6 text-primary opacity-45" />
                    }
                    content={
                        <PieChart
                            name=""
                            data={simUsageDoughnutData}
                            height={175}
                            className="p-4 md:p-1"
                            total={fixNumberPrecision(
                                simUsageDoughnutData?.reduce(
                                    (acc, item) => acc + item.value,
                                    0
                                )
                            )}
                            totalText="MB"
                        />
                    }
                />
            </div>
        </div>
    );
};
